/**
 * CustomGridEngine
 * Reusable engine for client-side search, filtering, merging variants, sorting, and pagination
 */
window.CustomGridEngine = function(config) {
  'use strict';

  const container = document.getElementById(config.containerId);
  if (!container) return;

  const collectionHandle = config.collectionHandle || null;
  const collectionProductCount = config.collectionProductCount || 0;
  const shopProductCount = config.shopProductCount || 0;
  const isSearchPage = config.isSearchPage || false;
  const resultsPerPage = config.resultsPerPage || 24;

  let allData = [];
  let isLoading = false;
  let hasInitialData = false;
  let paginationComplete = false;
  let skuIndexReady = false;

  let currentSort = 'relevance';
  let currentPage = 1;
  let isInitialLoad = true;
  let restoredScrollY = null;

  // Best practice 2026: Enable manual scroll restoration to prevent browser scroll jumps before DOM is loaded
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  // Restore state natively from history.state
  if (history.state) {
    if (history.state.currentPage) {
      currentPage = history.state.currentPage;
    }
    if (history.state.scrollY) {
      restoredScrollY = history.state.scrollY;
    }
    if (history.state.currentSort) {
      currentSort = history.state.currentSort;
    }
  } else {
    // Read from URL parameters if no history state is present
    const urlParams = new URLSearchParams(window.location.search);
    const urlSort = urlParams.get('sort_by');
    if (urlSort) {
      if (urlSort === 'price-ascending') currentSort = 'price-asc';
      else if (urlSort === 'price-descending') currentSort = 'price-desc';
      else if (urlSort === 'title-ascending') currentSort = 'alpha-az';
      else if (urlSort === 'title-descending') currentSort = 'alpha-za';
      else currentSort = 'relevance';
    }
  }

  // Cache settings
  const CACHE_KEY = 'cge-shopify-products-v60';
  const isDevelopment = window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost' || (window.Shopify && window.Shopify.designMode);
  const CACHE_EXPIRY_MS = isDevelopment ? 5000 : 60 * 60 * 1000; // 5 seconds in development, 1 hour in production

  // Search/Sort DOM Elements
  const searchInput = document.getElementById(config.searchInputId);
  const sortSelect = document.getElementById(config.sortId);
  const countDisplay = document.getElementById(config.countId);
  const gridContainer = document.getElementById(config.gridId);
  const paginationContainer = document.getElementById(config.paginationId);

  if (sortSelect) {
    sortSelect.value = currentSort;
  }

  const jsonProducts = [
    { name: 'Cloak Tuck 3.5 IWB Holster', handle: 'cloak-tuck-3-5-iwb-holster', dataUrl: config.cloakTuckJsonUrl, line: 'Cloak' },
    { name: 'Cloak Chest Holster', handle: 'cloak-chest-holster', dataUrl: config.cloakChestJsonUrl, line: 'Cloak' },
    { name: 'Cloak Chest Holster for Revolvers', handle: 'cloak-chest-holster-for-revolvers', dataUrl: config.cloakChestRevolverJsonUrl, line: 'Cloak' },
    { name: 'Cloak Shells', handle: 'cloak-shells', dataUrl: config.cloakShellJsonUrl, line: 'Cloak' },
    { name: 'Cloak Hook and Loop Holster', handle: 'cloak-hook-and-loop-holster', dataUrl: config.cloakHookJsonUrl, line: 'Cloak' },
    { name: 'Cloak Belt Holster', handle: 'cloak-belt-holster', dataUrl: config.cloakBeltJsonUrl, line: 'Cloak' },
    { name: 'Cloak Swivel Drop Leg Holster', handle: 'cloak-swivel-drop-leg-holster', dataUrl: config.cloakSwivelJsonUrl, line: 'Cloak' },
    { name: 'Cloak Tuck 3.0 Revolver Holsters', handle: 'cloak-tuck-3-0-revolver-holsters', dataUrl: config.cloakThreeZeroJsonUrl, line: 'Cloak' },
    { name: 'Cloak OWB Paddle Holster', handle: 'cloak-owb-paddle-holster', dataUrl: config.cloakPaddleJsonUrl, line: 'Cloak' }
  ];

  function getProductPriceInfo(item) {
    let handle = (item.productHandle || '').toLowerCase();
    let price = item.price || item.minPrice || 0;
    let compareAtPrice = item.compareAtPrice || null;
    
    // Fix backward sale pricing (where compare-at price is set lower than price in Shopify Admin)
    if (compareAtPrice !== null && price !== null && compareAtPrice > 0 && compareAtPrice < price) {
      const temp = price;
      price = compareAtPrice;
      compareAtPrice = temp;
    }
    
    let isSale = compareAtPrice > price;
    
    return { price, compareAtPrice, isSale };
  }

  // Price formatting helper
  function formatPrice(item) {
    const info = getProductPriceInfo(item);
    if (info.isSale) {
      const prefix = item.minPrice != null && item.price == null ? 'From ' : '';
      return `<span class="price-item price-item--sale price-item--last" style="color: #e84f35 !important; font-weight: bold; font-size: 12px; margin-right: 6px;">${prefix}$${info.price.toFixed(2)}</span><span><s class="price-item price-item--regular" style="color: #6b7280; font-size: 11px; text-decoration: line-through;">${prefix}$${info.compareAtPrice.toFixed(2)}</s></span>`;
    }
    if (item.price != null) return '$' + item.price.toFixed(2);
    if (item.minPrice != null) return 'From $' + item.minPrice.toFixed(2);
    return '';
  }

  // Parse tags safely into an array
  function parseTags(rawTags) {
    if (!rawTags) return [];
    if (Array.isArray(rawTags)) return rawTags.map(t => String(t).toLowerCase());
    return String(rawTags).split(',').map(t => t.trim().toLowerCase());
  }

  // Find variant ID by SKU from Shopify product JSON data
  function findVariantIdBySku(shopifyData, sku) {
    if (!shopifyData || !sku) return null;
    const product = shopifyData.product || shopifyData;
    if (!product.variants) return null;
    const variant = product.variants.find(v => String(v.sku).toLowerCase() === String(sku).toLowerCase());
    return variant ? variant.id : null;
  }

  // Find variant pricing by SKU from Shopify product JSON data
  function findVariantPricingBySku(shopifyData, sku) {
    if (!shopifyData || !sku) return null;
    const product = shopifyData.product || shopifyData;
    if (!product.variants) return null;
    const variant = product.variants.find(v => String(v.sku).toLowerCase() === String(sku).toLowerCase());
    if (!variant) return null;
    
    let price = variant.price ? parseFloat(variant.price) : null;
    let compareAtPrice = variant.compare_at_price ? parseFloat(variant.compare_at_price) : null;
    
    // Shopify Ajax API (/products/handle.js) returns prices in cents.
    // We can detect this by checking if the top-level keys 'product' or 'products' are missing.
    const isAjaxApi = !shopifyData.product && !shopifyData.products;
    if (isAjaxApi) {
      if (price !== null) price = price / 100;
      if (compareAtPrice !== null) compareAtPrice = compareAtPrice / 100;
    }
    
    return { price, compareAtPrice };
  }

  // Get shipping/priority badge HTML
  function getBadgeHtml(tags, handle, variantId, item) {
    let badgeHtml = '';
    const meta = (config.productMetadata && config.productMetadata[handle]) ? config.productMetadata[handle] : {};
    
    let leadTime = meta.lead_time || '';
    if (variantId && meta.variants && meta.variants[variantId]) {
      leadTime = meta.variants[variantId].lead_time || leadTime;
    }
    const leadTimeNum = parseInt(leadTime);
    
    let isReady = false;
    let isQuick = false;
    
    if (!isNaN(leadTimeNum)) {
      // Same ranges as the product page: 1-3 days Ready to Ship, 4-7 days Quick Ship, longer gets no badge
      isReady = (leadTimeNum >= 1 && leadTimeNum <= 3);
      isQuick = (leadTimeNum >= 4 && leadTimeNum <= 7);
    } else {
      isReady = tags.includes('ready to ship') || tags.includes('ready-to-ship');
      isQuick = !isReady;
    }

    if (handle.includes('cloak') || handle.includes('rapid-force') || handle.includes('holster')) {
      console.log(`[Badge Debug] Handle: ${handle}, leadTime: "${leadTime}", leadTimeNum: ${leadTimeNum}, tags: ${JSON.stringify(tags)}, isReady: ${isReady}, isQuick: ${isQuick}`);
    }

    if (isReady) {
      badgeHtml += `<div class="cge-badge ready-to-ship-badge show"><img src="https://cdn.shopify.com/s/files/1/0881/4854/1726/files/badge_ready.png?v=1761160941" alt="Ready to Ship" loading="eager" width="100" height="100"></div>`;
    } else if (isQuick) {
      badgeHtml += `<div class="cge-badge quick-ship-badge show"><img src="https://cdn.shopify.com/s/files/1/0881/4854/1726/files/badge_quick.png?v=1761160940" alt="Quick Ship" loading="eager" width="100" height="100"></div>`;
    }

    return badgeHtml;
  }

  // Get reviews rating star HTML
  function getRatingHtml(handle) {
    const meta = (config.productMetadata && config.productMetadata[handle]) ? config.productMetadata[handle] : {};
    const rating = parseFloat(meta.rating);
    const count = parseInt(meta.rating_count);
    if (isNaN(rating) || isNaN(count) || count <= 0) return '';

    const ratingMax = parseFloat(meta.rating_max) || 5.0;
    const decimal = rating % 1;
    let ratingDecimal = 0;
    if (decimal >= 0.3 && decimal <= 0.7) {
      ratingDecimal = 0.5;
    } else if (decimal > 0.7) {
      ratingDecimal = 1.0;
    }

    const ratingFloor = Math.floor(rating);

    return `
      <div class="rating-wrapper" style="display: inline-flex; align-items: center; gap: 6px;">
        <div class="rating" role="img" aria-label="${rating} out of ${ratingMax} stars">
          <span aria-hidden="true" class="rating-star" style="--rating: ${ratingFloor}; --rating-max: ${ratingMax}; --rating-decimal: ${ratingDecimal};"></span>
        </div>
        <span class="rating-count caption" style="color: rgba(var(--color-foreground), .6); font-size: 1.2rem;">(${count})</span>
      </div>
    `;
  }

  // Detect product line based on tags, titles, and vendors
  function getProductLine(product) {
    if (product.line) return product.line;
    
    const title = (product.productName || product.title || '').toLowerCase();
    const vendor = (product.vendor || '').toLowerCase();
    const tags = (product.tags || []).map(t => String(t).toLowerCase());

    if (title.includes('shapeshift') || title.includes('shape shift') || tags.includes('shapeshift') || tags.includes('shape shift')) {
      return 'ShapeShift';
    }
    if (title.includes('low-pro') || title.includes('low pro') || tags.includes('low-pro') || tags.includes('low pro')) {
      return 'Low-Pro';
    }
    if (title.includes('cloak') || tags.includes('cloak')) {
      return 'Cloak';
    }
    if (title.includes('rapid force') || tags.includes('rapid force') || vendor.includes('rapid force')) {
      return 'Rapid Force';
    }
    if (title.includes('photon') || tags.includes('photon') || vendor.includes('photon')) {
      return 'Photon';
    }
    return 'Other';
  }

  // IndexedDB Cache Database Wrapper
  const DB_NAME = 'cge-cache-db-v1';
  const DB_VERSION = 1;
  const STORE_NAME = 'cache-store';

  function getDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = function(e) {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = function(e) {
        resolve(e.target.result);
      };
      request.onerror = function(e) {
        reject(e.target.error);
      };
    });
  }

  async function idbGet(key) {
    try {
      const db = await getDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(key);
        req.onsuccess = function() {
          resolve(req.result || null);
        };
        req.onerror = function() {
          reject(req.error);
        };
      });
    } catch (e) {
      console.warn('IndexedDB read failed, falling back', e);
      return null;
    }
  }

  async function idbSet(key, val) {
    try {
      const db = await getDB();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const req = store.put(val, key);
        req.onsuccess = function() {
          resolve();
        };
        req.onerror = function() {
          reject(req.error);
        };
      });
    } catch (e) {
      console.warn('IndexedDB write failed', e);
    }
  }

  // Get/Save cache helpers using IndexedDB
  async function getProductsCache() {
    const cached = await idbGet(CACHE_KEY);
    if (!cached) return null;
    if (Date.now() - cached.timestamp > CACHE_EXPIRY_MS) {
      return null;
    }
    return cached.data;
  }

  async function saveProductsCache(products) {
    await idbSet(CACHE_KEY, { data: products, timestamp: Date.now() });
  }

  async function getCollectionCache(handle) {
    const key = 'cge-collection-v60-' + handle;
    const cached = await idbGet(key);
    if (!cached) return null;
    if (Date.now() - cached.timestamp > CACHE_EXPIRY_MS) {
      return null;
    }
    return cached.data;
  }

  async function saveCollectionCache(handle, products) {
    const key = 'cge-collection-v60-' + handle;
    await idbSet(key, { data: products, timestamp: Date.now() });
  }

  async function fetchProductJsonCached(handle) {
    const cacheKey = 'cge-product-json-' + handle;
    const cached = await idbGet(cacheKey);
    if (cached && (Date.now() - cached.timestamp < CACHE_EXPIRY_MS)) {
      return cached.data;
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      const data = await response.json();
      await idbSet(cacheKey, { data: data, timestamp: Date.now() });
      return data;
    } catch (e) {
      return null;
    }
  }

  async function fetchOfflineDbCached(handle, dataUrl) {
    if (!dataUrl) return null;
    const cacheKey = 'cge-offline-db-' + handle;
    const cached = await idbGet(cacheKey);
    if (cached && (Date.now() - cached.timestamp < CACHE_EXPIRY_MS)) {
      return cached.data;
    }

    try {
      const response = await fetch(dataUrl);
      if (!response.ok) return null;
      const data = await response.json();
      await idbSet(cacheKey, { data: data, timestamp: Date.now() });
      return data;
    } catch (e) {
      return null;
    }
  }

  function getColorCount(product, line) {
    if (!product) return null;
    let title = (product.title || product.name || '').toLowerCase();
    let handle = (product.handle || '').toLowerCase();
    if (title.includes('mag') || handle.includes('mag')) {
      return null;
    }
    let isCloakOrLowPro = (line && (line.toLowerCase() === 'cloak' || line.toLowerCase() === 'low-pro')) ||
                          title.includes('cloak') || title.includes('low-pro') || title.includes('low pro') ||
                          handle.includes('cloak') || handle.includes('low-pro') || handle.includes('low pro');

    if (product.options && Array.isArray(product.options) && product.variants && Array.isArray(product.variants)) {
      let colorOptIdx = -1;
      product.options.forEach((opt, idx) => {
        const name = (typeof opt === 'object' && opt.name) ? opt.name.toLowerCase() : String(opt).toLowerCase();
        if (name.includes('color') || name.includes('shell') || name.includes('finish')) {
          colorOptIdx = idx;
        }
      });
      if (colorOptIdx >= 0) {
        const availableColors = new Set();
        product.variants.forEach(variant => {
          if (variant.available) {
            const optVal = [variant.option1, variant.option2, variant.option3][colorOptIdx];
            if (optVal && optVal !== '-') {
              availableColors.add(optVal);
            }
          }
        });
        if (availableColors.size > 0) {
          return availableColors.size;
        }
      }
    }

    if (isCloakOrLowPro) {
      return 15;
    }

    return null;
  }

  function getImageSrcset(imageUrl) {
    if (!imageUrl) return '';
    const base = imageUrl.split('?')[0];
    const widths = [165, 360, 533, 720, 940, 1066];
    return widths.map(w => `${base}?width=${w}&format=webp ${w}w`).join(', ');
  }

  // Fetch collections page-by-page
  async function fetchCollectionProducts() {
    const cacheKey = collectionHandle + (currentSort === 'best-selling' ? '-best-selling' : '');
    const cached = await getCollectionCache(cacheKey);
    if (cached) return cached;

    const sortByParam = currentSort === 'best-selling' ? '&sort_by=best-selling' : '';

    if (collectionProductCount > 0) {
      const totalPages = Math.ceil(collectionProductCount / 250);
      try {
        const promises = [];
        for (let i = 1; i <= totalPages; i++) {
          promises.push(
            fetch(`/collections/${collectionHandle}/products.json?limit=250&page=${i}${sortByParam}`)
              .then(response => {
                if (!response.ok) throw new Error('HTTP ' + response.status);
                return response.json();
              })
          );
        }
        const results = await Promise.all(promises);
        const productsList = [];
        for (const data of results) {
          if (data.products && data.products.length > 0) {
            productsList.push(...data.products);
          }
        }
        if (productsList.length > 0) {
          await saveCollectionCache(cacheKey, productsList);
        }
        return productsList;
      } catch (err) {
        console.warn('Parallel collection fetch failed, falling back to sequential fetch:', err);
      }
    }

    let page = 1;
    let hasMore = true;
    const productsList = [];

    while (hasMore) {
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=250&page=${page}${sortByParam}`);
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const data = await response.json();
      
      if (data.products && data.products.length > 0) {
        productsList.push(...data.products);
        if (data.products.length === 250) {
          page++;
        } else {
          hasMore = false;
        }
      } else {
        hasMore = false;
      }
    }

    if (productsList.length > 0) {
      await saveCollectionCache(cacheKey, productsList);
    }
    return productsList;
  }

  // Fetch shopify products globally (search page)
  async function loadGlobalShopifyProducts() {
    const cached = await getProductsCache();
    if (cached) {
      cached.forEach(p => allData.push(p));
      hasInitialData = true;
      paginationComplete = true;
      runSearch();
      return;
    }

    let shopifyProducts = [];

    function processProductData(data) {
      if (data.products && data.products.length > 0) {
        data.products.forEach(product => {
          const isJsonProduct = jsonProducts.some(jp => jp.handle === product.handle);
          if (isJsonProduct) return;

          const productLine = getProductLine(product);
          const colorCount = getColorCount(product, productLine);

          let additionalSearchText = '';
          if (product.options && Array.isArray(product.options)) {
            product.options.forEach(opt => {
              if (opt && typeof opt === 'object') {
                if (opt.values && Array.isArray(opt.values)) {
                  additionalSearchText += ' ' + opt.values.join(' ');
                }
              } else {
                additionalSearchText += ' ' + String(opt);
              }
            });
          }

          if (product.variants && product.variants.length > 0 && product.options && product.options.length > 0) {
            const uniqueCombos = new Map();
            let manufacturerIndex = -1;
            let gunIndex = -1;

            product.options.forEach((opt, idx) => {
              const optName = opt.toLowerCase ? opt.toLowerCase() : (opt.name ? opt.name.toLowerCase() : '');
              if (optName.includes('manufacturer') || optName.includes('brand')) manufacturerIndex = idx;
              if (optName.includes('gun') || optName.includes('firearm') || optName.includes('model')) gunIndex = idx;
            });

            if (gunIndex >= 0 && manufacturerIndex < 0) {
              const manufacturer = product.vendor || 'Alien Gear Holsters';
              product.variants.forEach(variant => {
                const gun = [variant.option1, variant.option2, variant.option3][gunIndex];
                if (gun && gun !== '-') {
                  const key = `${manufacturer}|${gun}`;
                  if (!uniqueCombos.has(key)) {
                    let imageUrl = product.images?.[0]?.src || product.image?.src || null;
                    const price = variant.price ? parseFloat(variant.price) : null;
                    uniqueCombos.set(key, {
                      productName: product.title,
                      productHandle: product.handle,
                      variantId: variant.id,
                      manufacturer: manufacturer,
                      gun: gun,
                      imageUrl: imageUrl,
                      sku: variant.sku || '',
                      price: price,
                      compareAtPrice: variant.compare_at_price ? parseFloat(variant.compare_at_price) : null,
                      line: productLine,
                      tags: parseTags(product.tags),
                      searchText: `${product.title} ${manufacturer} ${gun} ${variant.sku || ''} ${productLine} ${additionalSearchText}`.toLowerCase(),
                      colorCount: colorCount
                    });
                  } else if (variant.sku) {
                    // Every variant's SKU must be searchable, not just the first one per gun
                    uniqueCombos.get(key).searchText += ' ' + String(variant.sku).toLowerCase();
                  }
                }
              });
            } else if (manufacturerIndex >= 0 && gunIndex >= 0) {
              product.variants.forEach(variant => {
                const optionValues = [variant.option1, variant.option2, variant.option3];
                const manufacturer = optionValues[manufacturerIndex];
                const gun = optionValues[gunIndex];

                if (manufacturer && gun && manufacturer !== '-' && gun !== '-') {
                  const key = `${manufacturer}|${gun}`;
                  if (!uniqueCombos.has(key)) {
                    let imageUrl = product.images?.[0]?.src || product.image?.src || null;
                    const price = variant.price ? parseFloat(variant.price) : null;
                    uniqueCombos.set(key, {
                      productName: product.title,
                      productHandle: product.handle,
                      variantId: variant.id,
                      manufacturer: manufacturer,
                      gun: gun,
                      imageUrl: imageUrl,
                      sku: variant.sku || '',
                      price: price,
                      compareAtPrice: variant.compare_at_price ? parseFloat(variant.compare_at_price) : null,
                      line: productLine,
                      tags: parseTags(product.tags),
                      searchText: `${product.title} ${manufacturer} ${gun} ${variant.sku || ''} ${productLine} ${additionalSearchText}`.toLowerCase(),
                      colorCount: colorCount
                    });
                  } else if (variant.sku) {
                    // Every variant's SKU must be searchable, not just the first one per gun
                    uniqueCombos.get(key).searchText += ' ' + String(variant.sku).toLowerCase();
                  }
                }
              });
            }
            uniqueCombos.forEach(combo => {
              allData.push(combo);
              shopifyProducts.push(combo);
            });
          }

          const productSku = product.variants?.[0]?.sku || '';
          let minPrice = null;
          let minComparePrice = null;
          if (product.variants && product.variants.length > 0) {
            let minVar = null;
            product.variants.forEach(v => {
              const p = v.price ? parseFloat(v.price) : 0;
              if (p > 0 && (minVar === null || p < parseFloat(minVar.price))) {
                minVar = v;
              }
            });
            if (minVar) {
              minPrice = parseFloat(minVar.price);
              minComparePrice = minVar.compare_at_price ? parseFloat(minVar.compare_at_price) : null;
            }
          }
          
          const productOnlyExists = allData.some(item =>
            item.productHandle === product.handle && !item.manufacturer && !item.gun
          );
          if (!productOnlyExists) {
            let imageUrl = product.images?.[0]?.src || product.image?.src || null;
            
            const productItem = {
              productName: product.title,
              productHandle: product.handle,
              variantId: product.variants?.[0]?.id || null,
              imageUrl: imageUrl,
              sku: productSku,
              minPrice: minPrice,
              compareAtPrice: minComparePrice,
              line: productLine,
              tags: parseTags(product.tags),
              searchText: `${product.title} ${productSku} ${productLine}${additionalSearchText}`.toLowerCase(),
              colorCount: colorCount
            };
            allData.push(productItem);
            shopifyProducts.push(productItem);
          }
        });
      }
    }

    if (shopProductCount > 0) {
      try {
        const r1 = await fetch(`/products.json?limit=24&page=1`);
        if (!r1.ok) throw new Error('HTTP ' + r1.status);
        const data1 = await r1.json();
        processProductData(data1);
        hasInitialData = true;
        runSearch();

        fetchRemainingGlobalProductsInBackground(shopifyProducts, processProductData);
        return;
      } catch (err) {
        console.warn('Parallel global products fetch failed, falling back to sequential fetch:', err);
      }
    }

    let hasMore = true;
    let page = 1;

    function fetchPage() {
      if (!hasMore) return;

      fetch(`/products.json?limit=250&page=${page}`)
        .then(r => r.json())
        .then(data => {
          if (data.products && data.products.length > 0) {
            processProductData(data);
            hasInitialData = true;

            if (data.products.length === 250) {
              page++;
              fetchPage();
            } else {
              hasMore = false;
              paginationComplete = true;
              saveProductsCache(shopifyProducts);
              runSearch();
            }
          } else {
            hasMore = false;
            hasInitialData = true;
            paginationComplete = true;
            runSearch();
          }
        })
        .catch(e => {
          hasMore = false;
          paginationComplete = true;
          if (shopifyProducts.length > 0) {
            saveProductsCache(shopifyProducts);
          }
          runSearch();
        });
    }
    fetchPage();
  }

  async function fetchRemainingGlobalProductsInBackground(shopifyProducts, processProductData) {
    const totalPages = Math.ceil(shopProductCount / 250);
    try {
      const promises = [];
      for (let i = 1; i <= totalPages; i++) {
        promises.push(
          fetch(`/products.json?limit=250&page=${i}`)
            .then(r => {
              if (!r.ok) throw new Error('HTTP ' + r.status);
              return r.json();
            })
        );
      }
      const results = await Promise.all(promises);

      // Deduplicate: filter out Shopify products added from the limit=24 fetch
      const shopifyHandles = new Set(shopifyProducts.map(p => p.productHandle));
      allData = allData.filter(item => !shopifyHandles.has(item.productHandle));
      shopifyProducts.length = 0;

      results.forEach(data => {
        processProductData(data);
      });
      paginationComplete = true;
      saveProductsCache(shopifyProducts);
      runSearch();
    } catch (err) {
      console.warn('Background global fetch failed:', err);
      paginationComplete = true;
    }
  }

  // Processed Data Cache (stores compiled allData array)
  async function getProcessedDataCache() {
    const key = 'cge-processed-data-v61-' + (collectionHandle || 'global') + (currentSort === 'best-selling' ? '-best-selling' : '');
    const cached = await idbGet(key);
    if (!cached) return null;
    if (Date.now() - cached.timestamp > CACHE_EXPIRY_MS) {
      return null;
    }
    return cached.data;
  }

  async function saveProcessedDataCache(data) {
    const key = 'cge-processed-data-v61-' + (collectionHandle || 'global') + (currentSort === 'best-selling' ? '-best-selling' : '');
    await idbSet(key, { data: data, timestamp: Date.now() });
  }

  // Process and compile fetched products
  async function processCollectionProducts(products, isComplete = true) {
    const tempAllData = [];
    const productOrderMap = new Map();
    products.forEach((p, index) => productOrderMap.set(p.handle, index));

    const collectionProductHandles = new Set(products.map(p => p.handle));

    // Load offline matching databases
    const activeJsonProducts = jsonProducts.filter(jp => collectionProductHandles.has(jp.handle));
    
    const jsonPromises = activeJsonProducts.map(product => {
      return Promise.all([
        fetchOfflineDbCached(product.handle, product.dataUrl),
        fetchProductJsonCached(product.handle)
      ])
      .then(([data, shopifyData]) => {
        if (data.variants && Array.isArray(data.variants)) {
          const productObj = shopifyData?.product || shopifyData;

          let basePrice = null;
          let baseCompareAtPrice = null;
          if (productObj) {
            const isAjax = !shopifyData.product && !shopifyData.products;
            const divisor = isAjax ? 100 : 1;
            
            if (productObj.price_min != null) {
              basePrice = parseFloat(productObj.price_min) / divisor;
            } else if (productObj.price != null) {
              basePrice = parseFloat(productObj.price) / divisor;
            }
            
            if (productObj.compare_at_price_min != null && parseFloat(productObj.compare_at_price_min) > 0) {
              baseCompareAtPrice = parseFloat(productObj.compare_at_price_min) / divisor;
            } else if (productObj.compare_at_price != null && parseFloat(productObj.compare_at_price) > 0) {
              baseCompareAtPrice = parseFloat(productObj.compare_at_price) / divisor;
            }

            // Fix backward sale pricing if compare-at is set lower than price
            if (baseCompareAtPrice !== null && basePrice !== null && baseCompareAtPrice < basePrice) {
              const temp = basePrice;
              basePrice = baseCompareAtPrice;
              baseCompareAtPrice = temp;
            }
          }

          const colorCount = getColorCount(productObj, product.line);
          const uniqueCombos = new Map();
          let imageUrl = null;
          if (productObj) {
            imageUrl = productObj.image?.src || productObj.featured_image || productObj.images?.[0]?.src || null;
          }

          const tagsParsed = parseTags(productObj?.tags);

          data.variants.forEach(variant => {
            if (variant.manufacturer && variant.gun) {
              const key = `${variant.manufacturer}|${variant.gun}`;
              if (!uniqueCombos.has(key)) {
                const shopifyPricing = findVariantPricingBySku(shopifyData, variant.sku);
                let price = shopifyPricing?.price || null;
                let compareAtPrice = shopifyPricing?.compareAtPrice || null;

                if (!price) {
                  const vPrice = variant.price ? parseFloat(variant.price) : null;
                  if (vPrice !== null && basePrice !== null && vPrice < basePrice) {
                    price = vPrice;
                    compareAtPrice = basePrice;
                  } else {
                    price = basePrice || vPrice;
                    compareAtPrice = baseCompareAtPrice;
                  }
                }

                uniqueCombos.set(key, {
                  productName: product.name,
                  productHandle: product.handle,
                  variantId: findVariantIdBySku(shopifyData, variant.sku),
                  manufacturer: variant.manufacturer,
                  gun: variant.gun,
                  imageUrl: imageUrl,
                  sku: variant.sku || '',
                  price: price,
                  compareAtPrice: compareAtPrice,
                  line: product.line,
                  tags: tagsParsed,
                  originalIndex: productOrderMap.get(product.handle) ?? 9999,
                  searchText: `${product.name} ${variant.manufacturer} ${variant.gun} ${variant.sku || ''} ${product.line}`.toLowerCase(),
                  colorCount: colorCount
                });
              } else if (variant.sku) {
                // Every variant's SKU must be searchable, not just the first one per gun
                uniqueCombos.get(key).searchText += ' ' + String(variant.sku).toLowerCase();
              }
            }
          });

          uniqueCombos.forEach(combo => tempAllData.push(combo));

          let minPrice = basePrice;
          let compareAtPrice = baseCompareAtPrice;
          
          if (minPrice === null) {
            minPrice = Math.min(...data.variants.filter(v => v.manufacturer && v.gun).map(v => v.price || 0).filter(p => p > 0));
          }
          
          const firstVariantSku = data.variants[0]?.sku || '';
          
          tempAllData.push({
            productName: product.name,
            productHandle: product.handle,
            variantId: (shopifyData?.product || shopifyData)?.variants?.[0]?.id || null,
            imageUrl: imageUrl,
            sku: firstVariantSku,
            minPrice: minPrice,
            compareAtPrice: compareAtPrice,
            line: product.line,
            tags: tagsParsed,
            originalIndex: productOrderMap.get(product.handle) ?? 9999,
            searchText: `${product.name} ${firstVariantSku} ${product.line}`.toLowerCase(),
            colorCount: colorCount
          });
        }
      })
      .catch(e => console.error(`Failed to load ${product.name} offline data:`, e));
    });

    await Promise.all(jsonPromises);

    products.forEach(product => {
      const isOfflineHandled = activeJsonProducts.some(ajp => ajp.handle === product.handle);
      if (isOfflineHandled) return;

      const uniqueCombos = new Map();
      const productLine = getProductLine(product);
      const colorCount = getColorCount(product, productLine);
      const originalIndex = productOrderMap.get(product.handle) ?? 9999;

      if (product.variants && product.variants.length > 0 && product.options && product.options.length > 0) {
        let manufacturerIndex = -1;
        let gunIndex = -1;

        product.options.forEach((opt, idx) => {
          const optName = opt.name ? opt.name.toLowerCase() : String(opt).toLowerCase();
          if (optName.includes('manufacturer') || optName.includes('brand')) manufacturerIndex = idx;
          if (optName.includes('gun') || optName.includes('firearm') || optName.includes('model')) gunIndex = idx;
        });

        if (gunIndex >= 0 && manufacturerIndex < 0) {
          const manufacturer = product.vendor || 'Alien Gear Holsters';
          product.variants.forEach(variant => {
            const options = [variant.option1, variant.option2, variant.option3];
            const gun = options[gunIndex];
            if (gun && gun !== '-') {
              const key = `${manufacturer}|${gun}`;
              if (!uniqueCombos.has(key)) {
                let imageUrl = product.images?.[0]?.src || null;
                const price = variant.price ? parseFloat(variant.price) : null;
                uniqueCombos.set(key, {
                  productName: product.title,
                  productHandle: product.handle,
                  variantId: variant.id,
                  manufacturer: manufacturer,
                  gun: gun,
                  imageUrl: imageUrl,
                  sku: variant.sku || '',
                  price: price,
                  compareAtPrice: variant.compare_at_price ? parseFloat(variant.compare_at_price) : null,
                  line: productLine,
                  tags: parseTags(product.tags),
                  originalIndex: originalIndex,
                  searchText: `${product.title} ${manufacturer} ${gun} ${variant.sku || ''} ${productLine}`.toLowerCase(),
                  colorCount: colorCount
                });
              } else if (variant.sku) {
                // Every variant's SKU must be searchable, not just the first one per gun
                uniqueCombos.get(key).searchText += ' ' + String(variant.sku).toLowerCase();
              }
            }
          });
        } else if (manufacturerIndex >= 0 && gunIndex >= 0) {
          product.variants.forEach(variant => {
            const options = [variant.option1, variant.option2, variant.option3];
            const manufacturer = options[manufacturerIndex];
            const gun = options[gunIndex];
            if (manufacturer && gun && manufacturer !== '-' && gun !== '-') {
              const key = `${manufacturer}|${gun}`;
              if (!uniqueCombos.has(key)) {
                let imageUrl = product.images?.[0]?.src || null;
                const price = variant.price ? parseFloat(variant.price) : null;
                uniqueCombos.set(key, {
                  productName: product.title,
                  productHandle: product.handle,
                  variantId: variant.id,
                  manufacturer: manufacturer,
                  gun: gun,
                  imageUrl: imageUrl,
                  sku: variant.sku || '',
                  price: price,
                  compareAtPrice: variant.compare_at_price ? parseFloat(variant.compare_at_price) : null,
                  line: productLine,
                  tags: parseTags(product.tags),
                  originalIndex: originalIndex,
                  searchText: `${product.title} ${manufacturer} ${gun} ${variant.sku || ''} ${productLine}`.toLowerCase(),
                  colorCount: colorCount
                });
              } else if (variant.sku) {
                // Every variant's SKU must be searchable, not just the first one per gun
                uniqueCombos.get(key).searchText += ' ' + String(variant.sku).toLowerCase();
              }
            }
          });
        }
      }

      uniqueCombos.forEach(combo => tempAllData.push(combo));

      const productSku = product.variants?.[0]?.sku || '';
      let minPrice = null;
      let minComparePrice = null;
      if (product.variants && product.variants.length > 0) {
        let minVar = null;
        product.variants.forEach(v => {
          const p = v.price ? parseFloat(v.price) : 0;
          if (p > 0 && (minVar === null || p < parseFloat(minVar.price))) {
            minVar = v;
          }
        });
        if (minVar) {
          minPrice = parseFloat(minVar.price);
          minComparePrice = minVar.compare_at_price ? parseFloat(minVar.compare_at_price) : null;
        }
      }
      
      let additionalSearchText = '';
      if (product.options && Array.isArray(product.options)) {
        product.options.forEach(opt => {
          if (opt && typeof opt === 'object') {
            if (opt.values && Array.isArray(opt.values)) {
              additionalSearchText += ' ' + opt.values.join(' ');
            }
          } else {
            additionalSearchText += ' ' + String(opt);
          }
        });
      }

      tempAllData.push({
        productName: product.title,
        productHandle: product.handle,
        variantId: product.variants?.[0]?.id || null,
        imageUrl: product.images?.[0]?.src || null,
        sku: productSku,
        minPrice: minPrice,
        compareAtPrice: minComparePrice,
        line: productLine,
        tags: parseTags(product.tags),
        originalIndex: originalIndex,
        searchText: `${product.title} ${productSku} ${productLine}${additionalSearchText}`.toLowerCase(),
        colorCount: colorCount
      });
    });

    allData = tempAllData;
    hasInitialData = true;
    if (isComplete) {
      paginationComplete = true;
      saveProcessedDataCache(allData);
    }
    runSearch();
  }

  // Fetch all collection products in parallel
  async function fetchAllCollectionProducts(sortByParam) {
    const totalPages = collectionProductCount > 0 ? Math.ceil(collectionProductCount / 250) : 10;
    const promises = [];
    for (let i = 1; i <= totalPages; i++) {
      promises.push(
        fetch(`/collections/${collectionHandle}/products.json?limit=250&page=${i}${sortByParam}`)
          .then(response => {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.json();
          })
      );
    }
    const results = await Promise.all(promises);
    const productsList = [];
    const seenHandles = new Set();

    for (const data of results) {
      if (data.products && data.products.length > 0) {
        data.products.forEach(p => {
          if (!seenHandles.has(p.handle)) {
            seenHandles.add(p.handle);
            productsList.push(p);
          }
        });
      }
    }
    return productsList;
  }

  // Perform full asynchronous load and compilation
  async function performFullLoad() {
    try {
      if (collectionHandle) {
        const cacheKey = collectionHandle + (currentSort === 'best-selling' ? '-best-selling' : '');
        const cached = await getCollectionCache(cacheKey);

        if (cached) {
          await processCollectionProducts(cached, true);
          return;
        }

        const sortByParam = currentSort === 'best-selling' ? '&sort_by=best-selling' : '';
        const productsList = await fetchAllCollectionProducts(sortByParam);

        if (productsList.length === 0) {
          countDisplay.textContent = '0 products';
          gridContainer.innerHTML = '<div class="cge__empty" style="grid-column: 1/-1;">No products found in this collection.</div>';
          lastRenderedHtml = null;
          return;
        }

        await saveCollectionCache(cacheKey, productsList);
        await processCollectionProducts(productsList, true);
      } else {
        loadGlobalShopifyProducts();
        loadJsonDataGlobal();
      }
    } catch (err) {
      console.error('Error during performFullLoad:', err);
      if (allData.length === 0) {
        countDisplay.textContent = 'Error loading products';
      }
    }
  }

  // Load and merge data
  async function init() {
    if (isLoading) return;
    isLoading = true;

    try {
      // 1. Try SWR - instant render from compiled cache
      const cached = await getProcessedDataCache();
      if (cached && cached.length > 0) {
        allData = cached;
        hasInitialData = true;
        paginationComplete = true;
        runSearch();
        isLoading = false;

        // Trigger background revalidation to keep cache fresh without blocking UI
        setTimeout(() => {
          performFullLoad().catch(e => console.error('Background revalidation failed:', e));
        }, 500);
        return;
      }

      // 2. Cold load - run full processing synchronously
      await performFullLoad();
      isLoading = false;
    } catch (err) {
      console.error('Error initializing CustomGridEngine:', err);
      countDisplay.textContent = 'Error loading products';
      isLoading = false;
    }
  }

  // Load variant JSON globally (search page only)
  function loadJsonDataGlobal() {
    jsonProducts.forEach(product => {
      Promise.all([
        fetchOfflineDbCached(product.handle, product.dataUrl),
        fetchProductJsonCached(product.handle)
      ])
      .then(([data, shopifyData]) => {
        if (data.variants && Array.isArray(data.variants)) {
          const productObj = shopifyData?.product || shopifyData;

          let basePrice = null;
          let baseCompareAtPrice = null;
          if (productObj) {
            const isAjax = !shopifyData.product && !shopifyData.products;
            const divisor = isAjax ? 100 : 1;
            
            if (productObj.price_min != null) {
              basePrice = parseFloat(productObj.price_min) / divisor;
            } else if (productObj.price != null) {
              basePrice = parseFloat(productObj.price) / divisor;
            }
            
            if (productObj.compare_at_price_min != null && parseFloat(productObj.compare_at_price_min) > 0) {
              baseCompareAtPrice = parseFloat(productObj.compare_at_price_min) / divisor;
            } else if (productObj.compare_at_price != null && parseFloat(productObj.compare_at_price) > 0) {
              baseCompareAtPrice = parseFloat(productObj.compare_at_price) / divisor;
            }

            // Fix backward sale pricing if compare-at is set lower than price
            if (baseCompareAtPrice !== null && basePrice !== null && baseCompareAtPrice < basePrice) {
              const temp = basePrice;
              basePrice = baseCompareAtPrice;
              baseCompareAtPrice = temp;
            }
          }

          const uniqueCombos = new Map();
          let imageUrl = productObj?.image?.src || productObj?.featured_image || productObj?.images?.[0]?.src || null;

          const tagsParsed = parseTags(productObj?.tags);

          data.variants.forEach(variant => {
            if (variant.manufacturer && variant.gun) {
              const key = `${variant.manufacturer}|${variant.gun}`;
              if (!uniqueCombos.has(key)) {
                const shopifyPricing = findVariantPricingBySku(shopifyData, variant.sku);
                let price = shopifyPricing?.price || null;
                let compareAtPrice = shopifyPricing?.compareAtPrice || null;

                if (!price) {
                  const vPrice = variant.price ? parseFloat(variant.price) : null;
                  if (vPrice !== null && basePrice !== null && vPrice < basePrice) {
                    price = vPrice;
                    compareAtPrice = basePrice;
                  } else {
                    price = basePrice || vPrice;
                    compareAtPrice = baseCompareAtPrice;
                  }
                }

                uniqueCombos.set(key, {
                  productName: product.name,
                  productHandle: product.handle,
                  variantId: findVariantIdBySku(shopifyData, variant.sku),
                  manufacturer: variant.manufacturer,
                  gun: variant.gun,
                  imageUrl: imageUrl,
                  sku: variant.sku || '',
                  price: price,
                  compareAtPrice: compareAtPrice,
                  line: product.line,
                  tags: tagsParsed,
                  searchText: `${product.name} ${variant.manufacturer} ${variant.gun} ${variant.sku || ''} ${product.line}`.toLowerCase()
                });
              } else if (variant.sku) {
                // Every variant's SKU must be searchable, not just the first one per gun
                uniqueCombos.get(key).searchText += ' ' + String(variant.sku).toLowerCase();
              }
            }
          });

          uniqueCombos.forEach(combo => allData.push(combo));

          const productOnlyExists = allData.some(item =>
            item.productHandle === product.handle && !item.manufacturer && !item.gun
          );
          if (!productOnlyExists) {
            let minPrice = basePrice;
            let compareAtPrice = baseCompareAtPrice;
            
            if (minPrice === null) {
              minPrice = Math.min(...data.variants.filter(v => v.manufacturer && v.gun).map(v => v.price || 0).filter(p => p > 0));
            }
            
            const firstVariantSku = data.variants[0]?.sku || '';
            
            allData.push({
              productName: product.name,
              productHandle: product.handle,
              variantId: (shopifyData?.product || shopifyData)?.variants?.[0]?.id || null,
              imageUrl: imageUrl,
              sku: firstVariantSku,
              minPrice: minPrice,
              compareAtPrice: compareAtPrice,
              line: product.line,
              tags: tagsParsed,
              searchText: `${product.name} ${firstVariantSku} ${product.line}`.toLowerCase()
            });
          }
        }
      })
      .catch(e => console.error(`Failed to load global ${product.name} offline data:`, e));
    });
  }

  // Filter, sort, and render
  function runSearch() {
    if (!paginationComplete) return;

    const query = searchInput.value.toLowerCase();
    const searchTerms = query.split(/\s+/).filter(t => t.length > 0);

    let results = allData;

    const SYNONYMS = {
      'cup': ['cup', 'mug', 'bottle'],
      'mug': ['mug', 'cup', 'bottle'],
      'bottle': ['bottle', 'cup', 'mug'],
      'bag': ['bag', 'fanny', 'cross-body'],
      'fanny': ['fanny', 'cross-body', 'bag'],
      'cross-body': ['cross-body', 'fanny', 'bag']
    };

    // Search filter
    if (searchTerms.length > 0) {
      results = results.filter(item => {
        return searchTerms.every(term => {
          const termSynonyms = SYNONYMS[term] || [term];
          return termSynonyms.some(syn => item.searchText.includes(syn));
        });
      });
    }

    // Exclude sub-variants if parent is matched
    const matchedParentHandles = new Set(
      results.filter(item => !item.manufacturer && !item.gun).map(item => item.productHandle)
    );
    if (matchedParentHandles.size > 0) {
      results = results.filter(item => {
        if (item.manufacturer || item.gun) {
          return !matchedParentHandles.has(item.productHandle);
        }
        return true;
      });
    }

    // Add products whose per-gun SKUs match (Cloak / Low-Pro / Rapid Force are single-variant in Shopify)
    if (skuIndexReady) {
      const rowsByHandle = new Map();
      window.AGHSkuSearch.match(searchTerms).items.forEach(row => {
        if (!rowsByHandle.has(row.handle)) rowsByHandle.set(row.handle, []);
        rowsByHandle.get(row.handle).push(row);
      });
      rowsByHandle.forEach((rows, handle) => {
        if (results.some(item => item.productHandle === handle)) return;
        const base = allData.find(item => item.productHandle === handle && !item.manufacturer && !item.gun) ||
                     allData.find(item => item.productHandle === handle) ||
                     { productName: rows[0].title, productHandle: handle, searchText: '' };
        const item = Object.assign({}, base);
        // A single matching gun links straight to its fitment
        if (new Set(rows.map(row => `${row.manufacturer}|${row.gun}`)).size === 1) {
          item.manufacturer = rows[0].manufacturer;
          item.gun = rows[0].gun;
        }
        results.push(item);
      });
    }

    // Deduplicate
    const deduped = new Map();
    results.forEach(item => {
      if (!deduped.has(item.productHandle)) {
        deduped.set(item.productHandle, item);
      }
    });
    results = Array.from(deduped.values());

    // Sort
    results = sortResults(results, currentSort);

    // Paginate
    const totalResults = results.length;
    const totalPages = Math.ceil(totalResults / resultsPerPage);
    
    let paginatedResults;
    if (isInitialLoad && currentPage > 1) {
      paginatedResults = results.slice(0, currentPage * resultsPerPage);
    } else {
      const start = (currentPage - 1) * resultsPerPage;
      paginatedResults = results.slice(start, start + resultsPerPage);
    }
    isInitialLoad = false;

    // Update URL if search page
    if (isSearchPage) {
      history.replaceState(null, '', `?q=${encodeURIComponent(query)}`);
    }

    renderResults(paginatedResults, totalResults, totalPages);
  }

  function sortResults(results, sortMode) {
    const sorted = [...results];
    switch (sortMode) {
      case 'price-asc':
        sorted.sort((a, b) => (a.price || a.minPrice || 0) - (b.price || b.minPrice || 0));
        break;
      case 'price-desc':
        sorted.sort((a, b) => (b.price || b.minPrice || 0) - (a.price || a.minPrice || 0));
        break;
      case 'alpha-az':
        sorted.sort((a, b) => a.productName.localeCompare(b.productName));
        break;
      case 'alpha-za':
        sorted.sort((a, b) => b.productName.localeCompare(a.productName));
        break;
      case 'best-selling':
      case 'relevance':
      default:
        sorted.sort((a, b) => {
          const indexA = typeof a.originalIndex === 'number' ? a.originalIndex : 9999;
          const indexB = typeof b.originalIndex === 'number' ? b.originalIndex : 9999;
          if (indexA !== indexB) {
            return indexA - indexB;
          }
          // Same product: parent cards first (items without gun/manufacturer variants)
          const isParentA = !a.gun && !a.manufacturer;
          const isParentB = !b.gun && !b.manufacturer;
          if (isParentA && !isParentB) return -1;
          if (!isParentA && isParentB) return 1;
          // Sort variants alphabetically by gun
          return (a.gun || '').localeCompare(b.gun || '');
        });
        break;
    }

    // Always demote shapeshift items to second to last, and replacement hardware / parts pack / replacement parts items to the very end of the list
    sorted.sort((a, b) => {
      const nameA = (a.productName || '').toLowerCase();
      const nameB = (b.productName || '').toLowerCase();
      
      const isHardwareA = nameA.includes('hardware') || nameA.includes('parts pack') || nameA.includes('screws') || nameA.includes('replacement parts');
      const isHardwareB = nameB.includes('hardware') || nameB.includes('parts pack') || nameB.includes('screws') || nameB.includes('replacement parts');

      const isShapeshiftA = nameA.includes('shapeshift') || nameA.includes('shape shift');
      const isShapeshiftB = nameB.includes('shapeshift') || nameB.includes('shape shift');

      let tierA = 0;
      if (isHardwareA) tierA = 2;
      else if (isShapeshiftA) tierA = 1;

      let tierB = 0;
      if (isHardwareB) tierB = 2;
      else if (isShapeshiftB) tierB = 1;

      if (tierA !== tierB) {
        return tierA - tierB;
      }
      return 0;
    });

    return sorted;
  }

  // Markup of the page-1 grid currently on screen, so repeat renders (cache, background refresh, ready checks) don't rebuild identical cards
  let lastRenderedHtml = null;

  function renderResults(results, totalResults, totalPages) {
    countDisplay.textContent = `${totalResults} product${totalResults !== 1 ? 's' : ''}`;

    if (results.length === 0) {
      if (currentPage === 1) {
        gridContainer.innerHTML = '<div class="cge__empty" style="grid-column: 1/-1;">No products found.</div>';
        lastRenderedHtml = null;
      }
      paginationContainer.innerHTML = '';
      return;
    }

    const cardsHtml = results.map((item, index) => {
      let productUrl = `/products/${item.productHandle}`;
      const params = new URLSearchParams();
      if (item.gun) params.append('gun', item.gun.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
      if (item.manufacturer) params.append('manufacturer', item.manufacturer.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
      if (params.toString()) productUrl += '?' + params.toString();

      const meta = (config.productMetadata && config.productMetadata[item.productHandle]) ? config.productMetadata[item.productHandle] : {};
      const isMag = (item.productName && item.productName.toLowerCase().includes('mag')) ||
                    (item.productHandle && item.productHandle.toLowerCase().includes('mag'));
      const isBase = (item.productName && item.productName.toLowerCase().includes('base')) ||
                     (item.productHandle && item.productHandle.toLowerCase().includes('base'));
      const isColorProduct = (meta.is_color_product === true ||
                              (item.colorCount && item.colorCount > 1) ||
                              item.line === 'Cloak' || 
                              item.line === 'Low-Pro' ||
                              (item.productName && (item.productName.toLowerCase().includes('cloak') || item.productName.toLowerCase().includes('low-pro') || item.productName.toLowerCase().includes('low pro'))) ||
                              (item.productHandle && (item.productHandle.toLowerCase().includes('cloak') || item.productHandle.toLowerCase().includes('low-pro') || item.productHandle.toLowerCase().includes('low pro'))) ||
                              (item.tags && item.tags.some(t => {
                                 const lt = String(t).toLowerCase();
                                 return lt.includes('cloak') || lt.includes('low-pro') || lt.includes('low pro');
                              }))) && !isMag && !isBase;

      const isAboveFold = (currentPage === 1 && index < 4);
      const isPhoton = item.productHandle && item.productHandle.toLowerCase().includes('photon-holster') && !item.productHandle.toLowerCase().includes('backer') && !item.productHandle.toLowerCase().includes('edc-pack');
      const isRapidForce = item.productHandle && item.productHandle.toLowerCase().includes('rapid-force') && (item.productHandle.toLowerCase().includes('holster') || item.productHandle.toLowerCase().includes('level-ii') || item.productHandle.toLowerCase().includes('level-2') || item.productHandle.toLowerCase().includes('level-iii') || item.productHandle.toLowerCase().includes('level-3')) && !item.productHandle.toLowerCase().includes('shell') && !item.productHandle.toLowerCase().includes('only') && !(item.productName || '').toLowerCase().includes('shell only') && !item.productHandle.toLowerCase().includes('adapter');

      return `
        <div class="custom-product-card">
          <a href="${productUrl}" class="custom-product-card__link">
             ${item.imageUrl ? `
              <div class="custom-product-card__image-wrapper">
                 <img src="${item.imageUrl ? item.imageUrl.split('?')[0] + '?width=533&format=webp' : ''}"
                      srcset="${getImageSrcset(item.imageUrl)}"
                      sizes="(min-width: 1200px) 266px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                      alt="${item.productName}"
                      class="custom-product-card__image"
                      loading="${isAboveFold ? 'eager' : 'lazy'}"
                      ${isAboveFold ? 'fetchpriority="high"' : ''}
                      width="533"
                      height="533"
                      style="aspect-ratio: 1 / 1; object-fit: cover;">
                 ${getBadgeHtml(item.tags || [], item.productHandle, item.variantId, item)}
                 ${(isColorProduct && !isPhoton && !isRapidForce) ? `<div class="cge-color-count-tag">Available in ${item.colorCount || 15} colors</div>` : ''}
                 ${/* isPhoton ? `<div class="cge-color-count-tag" style="background-color: #e84f35 !important; color: #ffffff !important;">Free Backer</div>` : ''}
                 ${/* isRapidForce ? `<div class="cge-color-count-tag" style="background-color: #e84f35 !important; color: #ffffff !important;">Free Surface Mount</div>` : */ ''}
              </div>
            ` : ''}
            <div class="custom-product-card__info" style="display: flex; flex-direction: column; align-items: flex-start;">
              <h3 class="custom-product-card__title" style="margin-bottom: 8px !important;">${item.productName}</h3>
              <div style="display: flex; justify-content: space-between; align-items: baseline; width: 100%; margin-top: auto; flex-wrap: wrap; gap: 8px;">
                ${getRatingHtml(item.productHandle) || '<div style="flex: 1;"></div>'}
                <p class="custom-product-card__price" style="margin: 0; margin-left: auto;">${formatPrice(item)}</p>
              </div>
            </div>
          </a>
        </div>
      `;
    }).join('');

    if (currentPage === 1 || gridContainer.innerHTML === '' || gridContainer.querySelector('.cge__empty')) {
      if (cardsHtml !== lastRenderedHtml) {
        gridContainer.innerHTML = cardsHtml;
        lastRenderedHtml = cardsHtml;
      }
    } else {
      gridContainer.insertAdjacentHTML('beforeend', cardsHtml);
      lastRenderedHtml = null;
    }

    // Restore scroll position
    if (typeof restoredScrollY === 'number') {
      setTimeout(() => {
        window.scrollTo(0, restoredScrollY);
        restoredScrollY = null;
      }, 150);
    }

    // Pagination (Load More Button)
    if (currentPage < totalPages) {
      paginationContainer.innerHTML = `
        <div class="cge__load-more-wrapper" style="text-align: center; margin: 3rem 0; width: 100%;">
          <button class="button button--secondary cge__load-more-btn" style="min-width: 220px; font-weight: 600;">Load More</button>
        </div>
      `;

      const loadMoreBtn = paginationContainer.querySelector('.cge__load-more-btn');
      if (loadMoreBtn) {
        loadMoreBtn.addEventListener('click', (e) => {
          e.preventDefault();
          currentPage++;
          runSearch();
        });
      }
    } else {
      paginationContainer.innerHTML = '';
    }
  }

  searchInput.addEventListener('input', () => {
    currentPage = 1;
    history.replaceState({ currentPage: 1, scrollY: 0, currentSort: currentSort, searchVal: searchInput.value }, '');
    runSearch();
  });

  searchInput.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' || e.key === 'Esc' || e.code === 'Escape' || e.keyCode === 27) {
      e.preventDefault();
      searchInput.value = '';
      searchInput.blur();
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  });

  sortSelect.addEventListener('change', (e) => {
    currentSort = e.target.value;
    
    // Update URL sort_by parameter
    const url = new URL(window.location.href);
    let shopifySort = 'relevance';
    if (currentSort === 'price-asc') shopifySort = 'price-ascending';
    else if (currentSort === 'price-desc') shopifySort = 'price-descending';
    else if (currentSort === 'alpha-az') shopifySort = 'title-ascending';
    else if (currentSort === 'alpha-za') shopifySort = 'title-descending';
    
    if (shopifySort !== 'relevance') {
      url.searchParams.set('sort_by', shopifySort);
    } else {
      url.searchParams.delete('sort_by');
    }
    
    currentPage = 1;
    history.replaceState({ currentPage: 1, scrollY: 0, currentSort: currentSort, searchVal: searchInput.value }, '', url.toString());
    
    if (currentSort === 'best-selling' || currentSort === 'relevance') {
      isLoading = false;
      init();
    } else {
      runSearch();
    }
  });

  const saveState = () => {
    history.replaceState({
      currentPage: currentPage,
      scrollY: window.scrollY,
      currentSort: currentSort,
      searchVal: searchInput ? searchInput.value : ''
    }, '');
  };
  window.addEventListener('pagehide', saveState);
  window.addEventListener('beforeunload', saveState);
  gridContainer.addEventListener('click', (e) => {
    if (e.target.closest('.custom-product-card__link')) {
      saveState();
    }
  });

  const prefetchUrls = new Set();
  const triggerPrefetch = (e) => {
    const cardLink = e.target.closest('.custom-product-card__link');
    if (cardLink) {
      const href = cardLink.getAttribute('href');
      if (href && !prefetchUrls.has(href)) {
        prefetchUrls.add(href);
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = href;
        document.head.appendChild(link);
      }
    }
  };
  gridContainer.addEventListener('mouseover', triggerPrefetch);
  gridContainer.addEventListener('touchstart', triggerPrefetch, { passive: true });

  // Pre-fill query if search page
  if (isSearchPage) {
    const params = new URLSearchParams(window.location.search);
    const query = params.get('q') || '';
    searchInput.value = query;
  }

  // Per-gun SKU lookup (assets/sku-search.js), search page only
  if (isSearchPage && window.AGHSkuSearch) {
    window.AGHSkuSearch.load()
      .then(() => {
        skuIndexReady = true;
        if (searchInput.value.trim()) runSearch();
      })
      .catch(e => console.error('[CustomGridEngine] Failed to load SKU index:', e));
  }

  // Trigger loading
  init();

  // Load triggers for search fallback
  const checkDataReady = setInterval(() => {
    if (hasInitialData && allData.length > 0) {
      clearInterval(checkDataReady);
      runSearch();
    }
  }, 100);

  setTimeout(() => {
    if (allData.length > 0 && countDisplay.textContent === 'Loading...') {
      runSearch();
    }
  }, 2000);
};
